import { useQuery } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useState } from "react";

import { useDataSource } from "@/adapters/data-source";
import { can, usePermissions } from "@/auth/permissions";
import { PhoneNumber } from "@/components/phone/PhoneNumber";
import { Fab } from "@/components/shell/Fab";
import { LoadFailed, NoMatches } from "@/components/shell/EmptyState";
import { TableSkeleton } from "@/components/shell/LoadingState";
import { RecordDrawer } from "@/components/shell/RecordDrawer";
import { renderCell } from "@/components/table/cells";
import { DataTable } from "@/components/table/DataTable";
import { Pagination } from "@/components/table/Pagination";
import { useCursorHistory } from "@/components/table/useCursorHistory";
import { Button } from "@/components/ui/button";
import { ContactDetail } from "@/modules/contacts/screens/ContactDetail";
import { ContactForm } from "@/modules/contacts/screens/ContactForm";
import { projectContact } from "@/modules/contacts/projections";
import { withContactType } from "@/modules/contacts/standing";
import { typeNamesOf } from "@/modules/contacts/typeNames";
import { useOpenRecord } from "@/routes/useOpenRecord";
import type { SectionScreenProps } from "@/modules/types";

/**
 * The Contacts list, and the sections that are the same list narrowed to one
 * system type — Vendors and Suppliers. (Customers is not one of them any
 * more: it lists Shopify's customers, `customers/`, #447. Nor is All Contacts,
 * which lists contacts and customers together, `directory/`, #493 — called
 * without a type this is still the plain contacts list, and nothing mounts it.)
 *
 * `typeName` is resolved to an id by looking the type up in `listTypes()`
 * rather than a hardcoded id: Vendor, Customer and Supplier are guaranteed
 * to exist and cannot be deleted (that guarantee is what makes this safe),
 * but nothing guarantees *which* id the adapter gave them. A hardcoded
 * fixture id here would work today and break the day the real API adapter
 * assigns its own ids.
 *
 * As with `ordersList`, the standing filter is applied on top of whatever
 * the user has built rather than replacing it, so filtering within Vendors
 * survives a re-render instead of being discarded.
 */
export function contactsList(typeName?: string) {
  return function ContactList({
    fields,
    filter,
    search,
    sort,
    onToggleSort,
    onClearFilter,
  }: SectionScreenProps) {
    const data = useDataSource();
    const permissions = usePermissions();

    const [limit, setLimit] = useState(25);
    /**
     * Paging stays local for the same reason `ordersList` keeps it local —
     * see the comment there. Selection is local too: there is no
     * `/contacts/:id` route to hold it (routes come from `config/modules.ts`,
     * which this module cannot edit), so which contact is open lives here
     * and resets, deliberately, on a page refresh.
     */
    const [history, setHistory] = useCursorHistory();
    const cursor = history[history.length - 1];
    const { recordId, open } = useOpenRecord();
    const [creating, setCreating] = useState(false);

    // Always fetched, not only on a type section: "New contact" needs the
    // full list for its type checkboxes on every section, All Contacts
    // included, and the query key is shared with `ContactTypes` and this
    // form's own lookup, so this is never a second request in practice.
    const typesQuery = useQuery({
      queryKey: ["contacts", "types"],
      queryFn: () => data.contacts.listTypes(),
    });

    const standingType = typesQuery.data?.find(
      (type) => type.name === typeName,
    );

    // Nothing to resolve for "All Contacts" (`typeName` undefined). For the
    // other three, wait for the type lookup before firing the list query —
    // otherwise this would fetch everyone first and the correctly-narrowed
    // page a moment later, a visible flash of the wrong rows.
    // Through `withContactType`, the function the export calls too.
    const effective =
      typeName !== undefined && standingType
        ? withContactType(standingType.id, filter)
        : filter;

    const query = useQuery({
      queryKey: ["contacts", "list", effective, search, sort, limit, cursor],
      queryFn: () =>
        data.contacts.list({ filter: effective, search, sort, limit, cursor }),
      enabled: typeName === undefined || standingType !== undefined,
    });

    if (typeName !== undefined) {
      if (typesQuery.isError) {
        return (
          <LoadFailed
            message={typesQuery.error.message}
            onRetry={() => {
              void typesQuery.refetch();
            }}
          />
        );
      }

      if (typesQuery.isPending) return <TableSkeleton />;

      // `typeName` names one of the three system types, which cannot be
      // deleted — this should be unreachable, but a query that filters by
      // "a type that no longer exists" is not the same question as "show me
      // everyone," so it fails loudly rather than quietly showing all rows.
      if (!standingType) {
        return (
          <LoadFailed message={`No contact type named "${typeName}" exists.`} />
        );
      }
    }

    if (query.isPending) return <TableSkeleton />;

    if (query.isError) {
      return (
        <LoadFailed
          message={query.error.message}
          onRetry={() => {
            void query.refetch();
          }}
        />
      );
    }

    return (
      <div className="flex h-full min-h-0 flex-col">
        {/*
          Hidden without `contacts.contact.create` — a courtesy, not the
          enforcement (FR-ACL-8): `POST /api/contacts` calls
          `requirePermission()` regardless, and a rejection from it would
          still render through `ContactForm`'s own `RejectionNotice`.
        */}
        {can(permissions, "contacts.contact.create") ? (
          <>
            <div className="max-md:hidden flex shrink-0 justify-end border-b px-4 py-2">
              <Button
                type="button"
                size="sm"
                onClick={() => {
                  setCreating(true);
                }}
              >
                <Plus />
                New contact
              </Button>
            </div>
            <Fab
              label="New contact"
              icon={<Plus aria-hidden />}
              onClick={() => {
                setCreating(true);
              }}
            />
          </>
        ) : null}

        <ContactForm
          mode="create"
          open={creating}
          onOpenChange={setCreating}
          initialTypeIds={standingType ? [standingType.id] : []}
          onSaved={(contact) => {
            // Back to page one so a stale cursor doesn't point past the end
            // once the newly-created row shifts everything after it — the
            // detail itself opens regardless of which page it lands on,
            // fetched by id rather than found in this page's rows.
            setHistory([undefined]);
            open(contact.id);
          }}
        />

        {recordId !== null ? (
          <RecordDrawer
            title={
              query.data.items.find((contact) => contact.id === recordId)
                ?.displayName ?? "Contact"
            }
          >
            <ContactDetail contactId={recordId} />
          </RecordDrawer>
        ) : null}
        <DataTable
          fields={fields}
          rows={query.data.items}
          getRowId={(contact) => contact.id}
          project={projectContact}
          // The Type column holds ids, which are not what anyone reads: the
          // names come from the types this screen already fetched (#514).
          renderOverride={(field, contact) => {
            if (field.name === "typeIds") {
              return renderCell(
                field,
                typeNamesOf(typesQuery.data, contact.typeIds),
              );
            }
            // One format and a Call button, as everywhere (#635).
            if (field.name === "phone" && contact.phone) {
              return (
                <PhoneNumber
                  number={contact.phone}
                  name={contact.displayName}
                  className="flex-nowrap whitespace-nowrap"
                />
              );
            }
            return undefined;
          }}
          sort={sort}
          onToggleSort={onToggleSort}
          onRowClick={(contact) => {
            open(contact.id);
          }}
          emptyState={<NoMatches onClear={onClearFilter} />}
          footer={
            <Pagination
              page={query.data}
              limit={limit}
              onLimitChange={(next) => {
                setLimit(next);
                setHistory([undefined]);
              }}
              history={history}
              onNavigate={setHistory}
            />
          }
        />
      </div>
    );
  };
}
